const API_BASE = (import.meta.env.VITE_GATEWAY_API_BASE_URL || "").replace(
  /\/+$/,
  "",
);

const SESSION_KEY = "geisha.webShop.session";
const LAST_ORDER_KEY = "geisha.webShop.lastOrderNo";

export type ShopSession = {
  accessToken: string;
  refreshToken: string;
  projectId: string;
  country?: string;
  xsollaLocale?: string;
  user: {
    id: string;
    email?: string | null;
    display_name?: string | null;
  };
};

type StoredShopSession = Partial<ShopSession> & {
  access_token?: unknown;
  refresh_token?: unknown;
  project_id?: unknown;
  country?: unknown;
  xsolla_locale?: unknown;
  xsollaLocale?: unknown;
  user?: unknown;
};

export type WebShopOfferPrice = {
  amount: string;
  amount_without_discount: string;
  currency: string;
};

export type WebShopOffer = {
  offer_code: string;
  provider: "xsolla";
  product_id: string;
  tokens_units: number;
  display_name?: string;
  description?: string;
  attributes?: Record<string, unknown>;
  price?: WebShopOfferPrice;
  can_be_bought?: boolean;
};

export type WalletBalance = {
  paid: number;
  free: number;
  total: number;
};

export type WebShopConfig = {
  back_to_game_url: string;
};

export type CheckoutResponse = {
  order_no: string;
  offer_code: string;
  tokens_units: number;
  checkout: {
    redirect_url: string;
  };
};

export type OrderStatus = {
  order_no: string;
  offer_code: string;
  status:
    | "created"
    | "paid_pending_fulfillment"
    | "paid_pending_claim"
    | "fulfilled"
    | "refunded"
    | "chargeback";
  tokens_units: number;
  ledger_entry_id?: string;
};

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function buildHeaders(session?: ShopSession): HeadersInit {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (session) {
    headers.Authorization = `Bearer ${session.accessToken}`;
    headers["x-project-id"] = session.projectId;
  }
  return headers;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

export function normalizeXsollaCountryPreference(
  value: unknown,
): string | undefined {
  const country = asString(value)?.toUpperCase();
  return country && /^[A-Z]{2}$/.test(country) ? country : undefined;
}

export function normalizeXsollaLocalePreference(
  value: unknown,
): string | undefined {
  const raw = asString(value)?.replace(/_/g, "-");
  if (!raw) return undefined;
  const [language, ...subtags] = raw.split("-");
  const locale = [
    language.toLowerCase(),
    ...subtags.map((subtag) => {
      if (/^[A-Za-z]{4}$/.test(subtag)) {
        return `${subtag[0]!.toUpperCase()}${subtag.slice(1).toLowerCase()}`;
      }
      if (/^[A-Za-z]{2}$/.test(subtag)) {
        return subtag.toUpperCase();
      }
      return subtag;
    }),
  ].join("-");
  return /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8}){0,3}$/.test(locale)
    ? locale
    : undefined;
}

function normalizeCheckoutLanguagePreference(value: unknown): string | undefined {
  const locale = normalizeXsollaLocalePreference(value);
  if (!locale) return undefined;
  const normalized = locale.toLowerCase();
  if (normalized.startsWith("zh-hant")) return "zh-Hant";
  if (normalized.startsWith("zh-hans")) return "zh-Hans";
  return locale.split("-")[0];
}

function normalizeStoredSession(value: unknown): ShopSession | null {
  if (!value || typeof value !== "object") return null;
  const stored = value as StoredShopSession;
  const accessToken =
    asString(stored.accessToken) ?? asString(stored.access_token);
  const refreshToken =
    asString(stored.refreshToken) ?? asString(stored.refresh_token);
  const projectId = asString(stored.projectId) ?? asString(stored.project_id);
  const country = normalizeXsollaCountryPreference(stored.country);
  const xsollaLocale = normalizeXsollaLocalePreference(
    stored.xsollaLocale ?? stored.xsolla_locale,
  );

  if (!accessToken || !refreshToken || !projectId) {
    return null;
  }

  return {
    accessToken,
    refreshToken,
    projectId,
    ...(country ? { country } : {}),
    ...(xsollaLocale ? { xsollaLocale } : {}),
    user:
      stored.user && typeof stored.user === "object"
        ? (stored.user as ShopSession["user"])
        : { id: "" },
  };
}

async function apiFetch<T>(
  path: string,
  options: RequestInit & { session?: ShopSession } = {},
): Promise<T> {
  const { session, headers, ...rest } = options;
  const response = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers: {
      ...buildHeaders(session),
      ...(headers ?? {}),
    },
  });
  const data = (await response.json().catch(() => null)) as unknown;
  if (!response.ok) {
    const error = data as {
      error?: { code?: string; message?: string };
    } | null;
    throw new ApiError(
      response.status,
      error?.error?.message ?? "Request failed",
      error?.error?.code,
    );
  }
  return data as T;
}

export function loadSession(): ShopSession | null {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    const session = normalizeStoredSession(JSON.parse(raw));
    if (!session) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    return session;
  } catch {
    sessionStorage.removeItem(SESSION_KEY);
    return null;
  }
}

export function saveSession(session: ShopSession): void {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(LAST_ORDER_KEY);
}

export function saveLastOrder(orderNo: string): void {
  sessionStorage.setItem(LAST_ORDER_KEY, orderNo);
}

export function loadLastOrder(): string | null {
  return sessionStorage.getItem(LAST_ORDER_KEY);
}

export async function exchangeHandoff(
  handoffToken: string,
  projectId: string,
): Promise<ShopSession> {
  const response = await apiFetch<{
    access_token: string;
    refresh_token: string;
    token_type: "Bearer";
    expires_in: number;
    user: ShopSession["user"];
  }>("/v1/auth/web_shop.handoff.exchange", {
    method: "POST",
    headers: {
      "x-project-id": projectId,
    },
    body: JSON.stringify({ handoff_token: handoffToken }),
  });

  return {
    accessToken: response.access_token,
    refreshToken: response.refresh_token,
    projectId,
    user: response.user,
  };
}

export async function listWebShopOffers(
  session: ShopSession,
  options: { country?: string; locale?: string } = {},
): Promise<WebShopOffer[]> {
  const params = new URLSearchParams();
  if (options.country) params.set("country", options.country);
  if (options.locale) params.set("locale", options.locale);
  const query = params.toString();
  const response = await apiFetch<{ offers: WebShopOffer[] }>(
    `/v1/iap/xsolla/web-shop-offers${query ? `?${query}` : ""}`,
    { session },
  );
  return response.offers;
}

export async function getBalance(
  session: ShopSession,
): Promise<WalletBalance> {
  return apiFetch<WalletBalance>("/v1/wallet/balance", { session });
}

export async function getWebShopConfig(
  session: ShopSession,
): Promise<WebShopConfig> {
  return apiFetch<WebShopConfig>("/v1/iap/xsolla/web-shop-config", {
    session,
  });
}

function createIdempotencyKey(): string {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }

  if (globalThis.crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("");
    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20),
    ].join("-");
  }

  return `web-shop-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 12)}`;
}

export async function createCheckout(
  session: ShopSession,
  offerCode: string,
  currency?: string,
  options: { country?: string; language?: string } = {},
): Promise<CheckoutResponse> {
  const idempotencyKey = createIdempotencyKey();
  const language = normalizeCheckoutLanguagePreference(options.language);
  return apiFetch<CheckoutResponse>("/v1/iap/payments/xsolla/checkout", {
    method: "POST",
    session,
    headers: {
      "Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify({
      offer_code: offerCode,
      idempotency_key: idempotencyKey,
      language,
      currency,
      country: options.country,
      return_url_key: "web_shop_v1",
      client: {
        platform: "web",
      },
    }),
  });
}

export async function getOrderStatus(
  session: ShopSession,
  orderNo: string,
): Promise<OrderStatus> {
  return apiFetch<OrderStatus>(`/v1/iap/orders/${encodeURIComponent(orderNo)}`, {
    session,
  });
}
